// openEHR RM model — BMM package org.openehr.rm.ehr_extract.generic_extract
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { LOCATABLE } from "../common/archetyped.ts";
import type { DV_CODED_TEXT } from "../data_types/text.ts";
import { EXTRACT_CONTENT_ITEM } from "./common.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Single item in generic extract, designed for 13606 and CDA data.
 */
export class GENERIC_CONTENT_ITEM extends EXTRACT_CONTENT_ITEM {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Identifier of model or schema used to create the content.
   */
  item_type?: DV_CODED_TEXT;
  /**
   * Internal storage for item_type_version
   * @protected
   */
  protected _item_type_version?: openehr_base.String;

  /**
   * Version of model or schema used to create the content item.
   */
  get item_type_version(): string | undefined {
    return this._item_type_version?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for item_type_version.
   * Use this to access openehr_base.String methods.
   */
  get $item_type_version(): openehr_base.String | undefined {
    return this._item_type_version;
  }

  /**
   * Sets item_type_version from either a primitive value or openehr_base.String wrapper.
   */
  set item_type_version(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._item_type_version = undefined;
    } else if (typeof val === "string") {
      this._item_type_version = openehr_base.String.from(val);
    } else {
      this._item_type_version = val;
    }
  }

  /**
   * Internal storage for author
   * @protected
   */
  protected _author?: openehr_base.String;

  /**
   * Reference to a demographic entity elsewhere in this Extract representing the author of the item version. The reference should be a UID corresponding to the UID of a \`GENERIC_CONTENT_ITEM\` containing the demographic information.
   */
  get author(): string | undefined {
    return this._author?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for author.
   * Use this to access openehr_base.String methods.
   */
  get $author(): openehr_base.String | undefined {
    return this._author;
  }

  /**
   * Sets author from either a primitive value or openehr_base.String wrapper.
   */
  set author(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._author = undefined;
    } else if (typeof val === "string") {
      this._author = openehr_base.String.from(val);
    } else {
      this._author = val;
    }
  }

  /**
   * Time of creation of this item version on the original system. This may be an earlier commit time, or it may be the time at which the item was created during the Extract generation process.
   */
  creation_time?: openehr_base.Iso8601_date_time;
  /**
   * Internal storage for authoriser
   * @protected
   */
  protected _authoriser?: openehr_base.String;

  /**
   * Reference to a demographic entity elsewhere in this Extract representing an authoriser of the item version, if relevant. The reference should be a UID corresponding to the UID of a \`GENERIC_CONTENT_ITEM\` containing the demographic information.
   */
  get authoriser(): string | undefined {
    return this._authoriser?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for authoriser.
   * Use this to access openehr_base.String methods.
   */
  get $authoriser(): openehr_base.String | undefined {
    return this._authoriser;
  }

  /**
   * Sets authoriser from either a primitive value or openehr_base.String wrapper.
   */
  set authoriser(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._authoriser = undefined;
    } else if (typeof val === "string") {
      this._authoriser = openehr_base.String.from(val);
    } else {
      this._authoriser = val;
    }
  }

  /**
   * Time of authorisation of this item version on the original system where relevant.
   */
  authorisation_time?: openehr_base.Iso8601_date_time;
  /**
   * Coded lifecycle status of the item.
   */
  item_status?: DV_CODED_TEXT;
  /**
   * Internal storage for version_id
   * @protected
   */
  protected _version_id?: openehr_base.String;

  /**
   * Version id of this item in original system.
   */
  get version_id(): string | undefined {
    return this._version_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for version_id.
   * Use this to access openehr_base.String methods.
   */
  get $version_id(): openehr_base.String | undefined {
    return this._version_id;
  }

  /**
   * Sets version_id from either a primitive value or openehr_base.String wrapper.
   */
  set version_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._version_id = undefined;
    } else if (typeof val === "string") {
      this._version_id = openehr_base.String.from(val);
    } else {
      this._version_id = val;
    }
  }

  /**
   * Internal storage for version_set_id
   * @protected
   */
  protected _version_set_id?: openehr_base.String;

  /**
   * Version set id of this item in original system, where applicable.
   */
  get version_set_id(): string | undefined {
    return this._version_set_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for version_set_id.
   * Use this to access openehr_base.String methods.
   */
  get $version_set_id(): openehr_base.String | undefined {
    return this._version_set_id;
  }

  /**
   * Sets version_set_id from either a primitive value or openehr_base.String wrapper.
   */
  set version_set_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._version_set_id = undefined;
    } else if (typeof val === "string") {
      this._version_set_id = openehr_base.String.from(val);
    } else {
      this._version_set_id = val;
    }
  }

  /**
   * Internal storage for system_id
   * @protected
   */
  protected _system_id?: openehr_base.String;

  /**
   * Identifier of EMR or other system from which the item was created / extracted. Typically in the form of a domain name.
   */
  get system_id(): string | undefined {
    return this._system_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for system_id.
   * Use this to access openehr_base.String methods.
   */
  get $system_id(): openehr_base.String | undefined {
    return this._system_id;
  }

  /**
   * Sets system_id from either a primitive value or openehr_base.String wrapper.
   */
  set system_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._system_id = undefined;
    } else if (typeof val === "string") {
      this._system_id = openehr_base.String.from(val);
    } else {
      this._system_id = val;
    }
  }

  /**
   * Other details about the content item.
   */
  other_details?: undefined;
  /**
   * Content object.
   */
  override item?: LOCATABLE = undefined;
}
